/* ==========================================================================
   Ure po meri — configurator as steps on a phone.

   On a narrow screen the theme drops the preview out of its sticky column, so
   every choice meant scrolling back up to see what the watch now looked like.
   Here the preview stays pinned and the options come one group at a time.

   Nothing in the configurator's own logic is touched: it keeps rebuilding the
   whole option list on every change, and a MutationObserver re-applies the step
   state afterwards. That way this works for both the generic configurator and
   the Royal frame one without either of them knowing about it.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('.m-cfg[data-cfg]');
  if (!root) return;
  var opts = root.querySelector('[data-cfg-opts]');
  if (!opts) return;
  var addons = root.querySelector('.m-cfg__addons');
  var mq = window.matchMedia('(max-width: 900px)');

  var activeKey = null;   // remembered by key, so a group appearing or vanishing
  var onAddons = false;   // (GMT, magnifier) does not throw the customer around
  var nav = null, next = null, applying = false;

  function groups() { return [].slice.call(opts.querySelectorAll('.m-cfg__opt')); }
  function total() { return groups().length + (addons ? 1 : 0); }

  function labelOf(el) {
    var l = el.querySelector('.m-cfg__label');
    if (!l) return '';
    // the label reads "Title <span>current value</span>"; only the title is wanted
    return (l.childNodes[0] && l.childNodes[0].textContent || '').trim();
  }

  function build() {
    if (nav) return;
    nav = document.createElement('div');
    nav.className = 'upm-cfg-nav';
    nav.innerHTML =
      '<button type="button" class="upm-cfg-nav__arrow" data-prev aria-label="Prejšnji korak">' +
        '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">' +
        '<path d="M15 5l-7 7 7 7" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
      '<div class="upm-cfg-nav__mid">' +
        '<span class="upm-cfg-nav__title" data-title></span>' +
        '<div class="upm-cfg-nav__bar"><span data-bar></span></div>' +
      '</div>' +
      '<span class="upm-cfg-nav__count" data-count></span>' +
      '<button type="button" class="upm-cfg-nav__arrow" data-next aria-label="Naslednji korak">' +
        '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">' +
        '<path d="M9 5l7 7-7 7" stroke-linecap="round" stroke-linejoin="round"/></svg></button>';
    opts.parentNode.insertBefore(nav, opts);

    next = document.createElement('button');
    next.type = 'button';
    next.className = 'upm-cfg-next';
    next.textContent = 'Naprej';
    opts.parentNode.insertBefore(next, opts.nextSibling);

    nav.querySelector('[data-prev]').addEventListener('click', function () { move(-1); });
    nav.querySelector('[data-next]').addEventListener('click', function () { move(1); });
    next.addEventListener('click', function () { move(1); });
  }

  function indexNow() {
    var g = groups();
    if (onAddons) return g.length;
    for (var i = 0; i < g.length; i++) if (g[i].dataset.key === activeKey) return i;
    return 0;
  }

  function move(dir) {
    var g = groups(), max = total() - 1;
    var i = Math.min(Math.max(indexNow() + dir, 0), max);
    onAddons = !!addons && i === g.length;
    activeKey = onAddons ? activeKey : (g[i] && g[i].dataset.key) || activeKey;
    apply();
    // The nav is sticky, so measuring IT gives back the position we are already
    // at. Measure the section instead, so preview + nav + options land together.
    var top = parseInt(getComputedStyle(root).getPropertyValue('--upm-cfg-top')) || 0;
    var y = root.getBoundingClientRect().top + window.pageYOffset - top - 6;
    if (Math.abs(y - window.pageYOffset) > 4) {
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }

  function apply() {
    if (applying) return;
    applying = true;

    var stepped = mq.matches;
    root.classList.toggle('m-cfg--stepped', stepped);
    if (!stepped) {
      groups().forEach(function (el) { el.classList.remove('is-cfg-step'); });
      root.classList.remove('is-addons-step');
      applying = false;
      return;
    }

    var g = groups();
    if (!g.length) { applying = false; return; }   // manifest still loading
    build();

    var i = indexNow();
    if (i >= g.length && !onAddons) { i = g.length - 1; }
    if (!onAddons) activeKey = g[i].dataset.key;

    g.forEach(function (el, idx) { el.classList.toggle('is-cfg-step', !onAddons && idx === i); });
    root.classList.toggle('is-addons-step', onAddons);

    var pos = onAddons ? g.length : i;
    var max = total();
    nav.querySelector('[data-count]').textContent = (pos + 1) + '/' + max;
    nav.querySelector('[data-title]').textContent = onAddons ? 'Dodatki' : labelOf(g[i]);
    nav.querySelector('[data-bar]').style.width = ((pos + 1) / max * 100) + '%';
    nav.querySelector('[data-prev]').disabled = pos === 0;
    nav.querySelector('[data-next]').disabled = pos === max - 1;
    next.hidden = pos === max - 1;

    applying = false;
  }

  // Offset the pinned preview by the header ONLY if that header actually stays
  // on screen. This theme scrolls its header away, so reserving its height left
  // a permanent gap above the watch with the page running through it — which is
  // what made the image look like it was still moving. Measured rather than
  // assumed, so it self-corrects if a sticky header is switched on later.
  function stuckHeaderHeight() {
    var els = document.querySelectorAll('header, .m-header__mobile, .m-header__desktop, .m-topbar, [class*="header-sticky"]');
    var h = 0;
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (!el.offsetHeight) continue;              // not rendered at this width
      var pos = getComputedStyle(el).position;
      if (pos === 'fixed' || pos === 'sticky') h = Math.max(h, el.getBoundingClientRect().height);
    }
    return Math.round(h);
  }

  // the nav then sits below the preview, whatever height that turns out to be
  function setTop() {
    var h = stuckHeaderHeight();
    root.style.setProperty('--upm-cfg-top', (mq.matches ? h : 0) + 'px');
    var pv = root.querySelector('.m-cfg__preview');
    if (pv && mq.matches) {
      root.style.setProperty('--upm-cfg-preview', Math.round(pv.getBoundingClientRect().height) + 'px');
    }
  }

  // the own-logo add-on refuses the cart while its panel is empty; on a phone
  // that panel sits on the last step, so take the customer there to see why
  root.addEventListener('upm-cfg:addons', function () {
    if (!mq.matches || !addons || onAddons) return;
    onAddons = true;
    apply();
  });

  // the configurator rewrites the whole option list on every change
  new MutationObserver(function () { apply(); }).observe(opts, { childList: true });
  if (mq.addEventListener) mq.addEventListener('change', function () { setTop(); apply(); });

  setTop();
  window.addEventListener('resize', setTop);
  window.addEventListener('load', setTop);
  // the stage fills in asynchronously; remeasure once it has
  var stage = root.querySelector('[data-cfg-stage]');
  if (stage && 'ResizeObserver' in window) new ResizeObserver(setTop).observe(stage);
  apply();
})();
